import {
  Box,
  Flex,
  Icon,
  IconButton,
  Input,
  Tag,
  TagLabel,
  Text,
  useToast,
  VisuallyHidden,
  type SystemStyleObject,
} from "@chakra-ui/react";
import type { Content } from "@tiptap/react";
import { Editor } from "@tiptap/react";
import { components, OptionProps, Select } from "chakra-react-select";
import { t } from "i18next";
import { forwardRef, useCallback, useId, useImperativeHandle, useRef, useState } from "react";
import { FaFileImport, FaHighlighter, FaImage, FaLink, FaListOl, FaListUl, FaMagic, FaTable, FaTimes } from "react-icons/fa";
import { TextDocumentDto, useListDocumentTemplatesQuery } from "../../../autogen/bff-api";
import { useSub } from "../../subscription/useSub";
import { extractImageUploadIds, withoutImageNodes } from "./imageNodes";
import { parseTiptapContent, TipTapCopyTemplateDocumentsHandler } from "./TipTap";
import { ToolbarButton } from "./ToolbarButton";
import { useRovingToolbar } from "./useRovingToolbar";
import { useSelectScreenReaderMessages } from "../Selector/useSelectScreenReaderMessages";

const rgbToHex = (rgb?: string) => {
  const regexRes = rgb?.match(/\d+/g)?.map(Number);
  if (!regexRes) return;
  const [r, g, b] = regexRes;
  if (!(isFinite(r) && isFinite(g) && isFinite(b))) return;
  return (
    "#" +
    [r, g, b]
      .map((x) => {
        const hex = x.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      })
      .join("")
  );
};

const replaceUploadIdsAndSrcInContent = ({
  content,
  uploadIdMapping,
  srcMapping,
}: {
  content: unknown;
  uploadIdMapping: Record<string, string>;
  srcMapping: Record<string, string>;
}): unknown => {
  if (!content || typeof content !== "object") return content;

  const node = content as Record<string, unknown>;

  if (node.type === "image" && node.attrs && typeof node.attrs === "object") {
    const attrs = node.attrs as Record<string, unknown>;
    const oldUploadId = attrs.uploadId as string | undefined;
    if (oldUploadId) {
      const newAttrs = { ...attrs };
      if (uploadIdMapping[oldUploadId]) {
        newAttrs.uploadId = uploadIdMapping[oldUploadId];
      }
      if (srcMapping[oldUploadId]) {
        newAttrs.src = srcMapping[oldUploadId];
      }
      return { ...node, attrs: newAttrs };
    }
  }

  if (Array.isArray(node.content)) {
    return {
      ...node,
      content: node.content.map((child) =>
        replaceUploadIdsAndSrcInContent({ content: child, uploadIdMapping, srcMapping })
      ),
    };
  }

  return node;
};

export interface ToolbarConfig {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  headings?: boolean;
  colors?: boolean;
  lists?: boolean;
  link?: boolean;
  table?: boolean;
  image?: boolean;
  template?: boolean;
  autofill?: boolean;
}

/** Lets the editor hand focus to the toolbar — see the Alt+F10 binding in `TipTap.tsx`. */
export type MenuBarHandle = { focus: () => void };

export const MenuBar = forwardRef<
  MenuBarHandle,
  {
    editor?: Editor;
    onImageSelect?: (files: File[]) => void;
    onCopyTemplateDocuments?: TipTapCopyTemplateDocumentsHandler;
    onAutofill?: () => void;
    toolbar?: ToolbarConfig;
  }
>(({ editor, onImageSelect, onCopyTemplateDocuments, onAutofill, toolbar }, ref) => {
  const selectScreenReaderMessages = useSelectScreenReaderMessages<TemplateOption>();
  const toolbarConfig: Required<ToolbarConfig> = {
    bold: true, italic: true, underline: true,
    headings: true, colors: true, lists: true,
    link: true, table: true, image: true, template: true, autofill: true,
    ...toolbar,
  };

  type TemplateOption = { label: string; value: string; tags: string[] };

  const toast = useToast();

  const sub = useSub();

  const { data: documentTemplatesData, isLoading } = useListDocumentTemplatesQuery(undefined, {
    skip: !toolbarConfig.template,
  });

  const colorInputRef = useRef<HTMLInputElement>(null);
  const backgroundColorInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const [showTemplateSelector, setShowTemplateSelector] = useState(false);

  const triggerImageInput = () => {
    imageInputRef.current?.click();
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0 && onImageSelect) {
      onImageSelect(Array.from(files));
    }
    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
  };

  const triggerColorInput = () => {
    colorInputRef.current?.click();
  };

  const triggerBackgroundColorInput = () => {
    backgroundColorInputRef.current?.click();
  };

  const handleTemplateSelect = useCallback(
    async (template: TextDocumentDto) => {
      if (!editor) return;

      const content = parseTiptapContent(template.text);
      const hasAttachments = template.documents.length > 0 || extractImageUploadIds(content).length > 0;

      if (hasAttachments && onCopyTemplateDocuments) {
        try {
          const { uploadIdMapping, srcMapping } = await onCopyTemplateDocuments({
            templateDocuments: template.documents,
          });
          const updatedContent = replaceUploadIdsAndSrcInContent({ content, uploadIdMapping, srcMapping }) as Content;
          editor.commands.insertContent(updatedContent);
        } catch (error) {
          toast({
            variant: "subtle",
            status: "error",
            title: t("Failed to insert template"),
            description: t("Could not copy template documents"),
          });
          return;
        }
      } else if (hasAttachments) {
        editor.commands.insertContent(withoutImageNodes(content) as Content);
        toast({
          variant: "subtle",
          status: "warning",
          title: t("Content inserted without images"),
          description: t("This field cannot hold attachments, so the images in the template were left out."),
        });
        setShowTemplateSelector(false);
        return;
      } else {
        editor.commands.insertContent(content);
      }

      toast({
        variant: "subtle",
        status: "success",
        title: t("Content inserted"),
        description: `${t("Inserted content from the template called")} ${template.name}`,
      });
      setShowTemplateSelector(false);
    },
    [editor, onCopyTemplateDocuments, toast]
  );

  const setLink = useCallback(() => {
    if (!editor) return;

    if (editor.isActive("link")) {
      editor.chain().focus().unsetLink().run();
      return;
    }

    const protocol = "https://";

    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL", !previousUrl ? protocol : previousUrl);

    if (url === null) return;

    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: url.startsWith(protocol) ? url : protocol + url })
      .run();
  }, [editor]);

  const keyboardHintId = useId();

  const { containerRef, focusCurrent, toolbarHandlers } = useRovingToolbar({
    onEscape: () => editor?.commands.focus(),
  });

  useImperativeHandle(ref, () => ({ focus: focusCurrent }), [focusCurrent]);

  if (!editor) return null;

  if (showTemplateSelector)
    return (
      <Flex width="full">
        <Select<TemplateOption>
          {...selectScreenReaderMessages}
          unstyled
          placeholder={`${t("Select document template")}...`}
          isLoading={isLoading}
          options={documentTemplatesData?.documents.map((doc) => ({
            label: doc.name ? doc.name : (t("No title") ?? ""),
            value: doc.id,
            tags: doc.tags,
          }))}
          filterOption={(option: { label: string; data: TemplateOption }, inputValue: string) => {
            const labelMatch = option.label.toLowerCase().includes(inputValue.toLowerCase());
            const tagsMatch = option.data.tags.some((tag) => tag.toLowerCase().includes(inputValue.toLowerCase()));
            return labelMatch || tagsMatch;
          }}
          chakraStyles={{
            container: (provided: SystemStyleObject) => ({
              ...provided,
              width: "full",
            }),
            control: (provided: SystemStyleObject) => ({
              ...provided,
              rounded: "md",
              roundedBottom: "none",
              roundedRight: "none",
              borderTop: "none",
              borderLeft: "none",
              borderRight: "none",
              _focus: { border: "none" },
            }),
            menu: (provided: SystemStyleObject) => ({ ...provided, rounded: "md" }),
            menuList: (provided: SystemStyleObject) => ({ ...provided, rounded: "md" }),
          }}
          components={{ Option: CustomOption }}
          onChange={(option: TemplateOption | null) => {
            const template = documentTemplatesData?.documents.find((d) => d.id === option?.value);
            if (template) {
              handleTemplateSelect(template);
            }
          }}
        />
        <IconButton
          aria-label="close template selector"
          icon={<Icon as={FaTimes} />}
          roundedLeft="0"
          roundedBottom="0"
          onClick={() => setShowTemplateSelector(false)}
        />
      </Flex>
    );

  return (
    <>
      {/* Announced when focus enters the toolbar; the sighted equivalent is EditorKeyboardHint. */}
      <VisuallyHidden id={keyboardHintId}>
        {t("Arrow keys move between the buttons. Escape returns to the text.")}
      </VisuallyHidden>
      <Flex
        ref={containerRef}
        role="toolbar"
        aria-label={t("Text formatting") ?? "Text formatting"}
        aria-describedby={keyboardHintId}
        aria-orientation="horizontal"
        columnGap="1"
        rowGap="1"
        flexWrap="wrap"
        justifyContent="center"
        py="2"
        {...toolbarHandlers}
      >
        {toolbarConfig.bold && (
          <ToolbarButton
            label={t("Bold")}
            isPressed={editor.isActive("bold")}
            onClick={() => editor.chain().focus().toggleBold().run()}
            fontWeight={"bold"}
          >
            {t("B")}
          </ToolbarButton>
        )}
        {toolbarConfig.italic && (
          <ToolbarButton
            label={t("Italic")}
            isPressed={editor.isActive("italic")}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            fontStyle={"italic"}
          >
            {t("I")}
          </ToolbarButton>
        )}
        {toolbarConfig.underline && (
          <ToolbarButton
            label={t("Underline")}
            isPressed={editor.isActive("underline")}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            textDecoration={"underline"}
          >
            {t("U")}
          </ToolbarButton>
        )}
        {toolbarConfig.headings && (
          <>
            <ToolbarButton
              label={t("Heading 1")}
              isPressed={editor.isActive("heading", { level: 1 })}
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
              fontSize={"sm"}
            >
              {t("H1")}
            </ToolbarButton>
            <ToolbarButton
              label={t("Heading 2")}
              isPressed={editor.isActive("heading", { level: 2 })}
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              fontSize={"xs"}
            >
              {t("H2")}
            </ToolbarButton>
            <ToolbarButton
              label={t("Heading 3")}
              isPressed={editor.isActive("heading", { level: 3 })}
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              fontSize={"xx-small"}
            >
              {t("H3")}
            </ToolbarButton>
          </>
        )}
        {toolbarConfig.colors && (
          <>
            <Box position="relative" width="6" minWidth="6">
              <Input
                ref={colorInputRef}
                tabIndex={-1}
                type="color"
                size="xs"
                width="6"
                opacity="0"
                position="absolute"
                border="none"
                p="0"
                value={rgbToHex(editor.getAttributes("textStyle").color) ?? "#000000"}
                onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
              />
              <ToolbarButton
                label={t("Text colour")}
                border="1px solid"
                borderColor="smBorder"
                backgroundColor="smBackground"
                color={editor.getAttributes("textStyle").color ?? "inherit"}
                position="absolute"
                top="0"
                rounded="full"
                p="0"
                onClick={triggerColorInput}
              >
                A
              </ToolbarButton>
            </Box>
            <Box position="relative" width="6" minWidth="6">
              <Input
                ref={backgroundColorInputRef}
                tabIndex={-1}
                type="color"
                size="xs"
                width="6"
                opacity="0"
                position="absolute"
                border="none"
                p="0"
                value={editor.getAttributes("highlight").color ?? "#ffffff"}
                onChange={(e) => editor.chain().focus().setHighlight({ color: e.target.value }).run()}
              />
              <ToolbarButton
                label={t("Highlight colour")}
                border="1px solid"
                borderColor="smBorder"
                backgroundColor={editor.getAttributes("highlight").color ?? "smBackground"}
                position="absolute"
                top="0"
                rounded="full"
                cursor="pointer"
                p="1"
                onClick={triggerBackgroundColorInput}
              >
                <Icon as={FaHighlighter} />
              </ToolbarButton>
            </Box>
          </>
        )}
        {toolbarConfig.lists && (
          <>
            <ToolbarButton
              label={t("Bullet list")}
              isPressed={editor.isActive("bulletList")}
              cursor="pointer"
              onClick={() => editor.chain().focus().toggleBulletList().run()}
            >
              <Icon as={FaListUl} />
            </ToolbarButton>
            <ToolbarButton
              label={t("Numbered list")}
              isPressed={editor.isActive("orderedList")}
              cursor="pointer"
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
            >
              <Icon as={FaListOl} />
            </ToolbarButton>
          </>
        )}
        {toolbarConfig.link && (
          <ToolbarButton
            label={t("Insert link")}
            isPressed={editor.isActive("link")}
            onClick={setLink}
            fontSize={"xx-small"}
          >
            <Icon as={FaLink} />
          </ToolbarButton>
        )}
        {toolbarConfig.table && (
          <ToolbarButton
            label={t("Insert table")}
            onClick={() => editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
            isDisabled={editor.isActive("table")}
            backgroundColor={editor.isActive("table") ? "smBackgroundTertiary" : undefined}
            fontSize={"xx-small"}
          >
            <Icon as={FaTable} />
          </ToolbarButton>
        )}
        {toolbarConfig.image && onImageSelect && (
          <>
            <Input
              ref={imageInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              display="none"
              onChange={handleImageChange}
            />
            <ToolbarButton label={t("Insert image")} onClick={triggerImageInput} fontSize={"xx-small"}>
              <Icon as={FaImage} />
            </ToolbarButton>
          </>
        )}
        {toolbarConfig.table && editor.isActive("table") && (
          <Flex
            rowGap="1"
            flexDirection="column"
            overflow="visible"
            height="max-content"
            backgroundColor="smBackgroundSecondary"
            rounded="md"
            zIndex="2"
            shadow="sm"
            px="1"
            pb="1"
          >
            <ToolbarButton
              zIndex="10"
              minHeight="6"
              px="1"
              onClick={() => editor.chain().focus().addRowAfter().run()}
              fontSize={"xx-small"}
            >
              {t("Add row")}
            </ToolbarButton>
            <ToolbarButton
              zIndex="10"
              minHeight="6"
              px="1"
              onClick={() => editor.chain().focus().addColumnAfter().run()}
              fontSize={"xx-small"}
            >
              {t("Add column")}
            </ToolbarButton>
            <ToolbarButton
              zIndex="10"
              minHeight="6"
              px="1"
              onClick={() => editor.chain().focus().deleteRow().run()}
              fontSize={"xx-small"}
            >
              {t("Delete row")}
            </ToolbarButton>
            <ToolbarButton
              zIndex="10"
              minHeight="6"
              px="1"
              onClick={() => editor.chain().focus().deleteColumn().run()}
              fontSize={"xx-small"}
            >
              {t("Delete column")}
            </ToolbarButton>
            <ToolbarButton
              zIndex="10"
              minHeight="6"
              px="1"
              onClick={() => editor.chain().focus().deleteTable().run()}
              fontSize={"xx-small"}
            >
              {t("Delete table")}
            </ToolbarButton>
          </Flex>
        )}
        {toolbarConfig.template && sub.hasDocumentTemplateAccess && (
          <ToolbarButton
            colorScheme="teal"
            onClick={() => setShowTemplateSelector(true)}
            fontSize={"xx-small"}
            leftIcon={<Icon as={FaFileImport} />}
          >
            {t("Insert template")}
          </ToolbarButton>
        )}
        {toolbarConfig.autofill && sub.hasAiAccess && onAutofill && (
          <ToolbarButton
            colorScheme="teal"
            variant="outline"
            onClick={onAutofill}
            fontSize={"xx-small"}
            leftIcon={<Icon as={FaMagic} />}
          >
            {t("Autofill")}
          </ToolbarButton>
        )}
      </Flex>
    </>
  );
});

MenuBar.displayName = "MenuBar";

const CustomOption = (props: OptionProps<{ label: string; value: string; tags: string[] }, false>) => {
  return (
    <components.Option {...props}>
      <Flex alignItems="center" _hover={{ backgroundColor: "smBackgroundSecondary", cursor: "pointer" }} p="2" m="0">
        <Text fontSize="sm" pr="1">
          {props.label}
        </Text>
        <Flex columnGap="1">
          {props.data.tags.map((tag) => (
            <Tag key={tag} size="sm" alignItems="center">
              <TagLabel>{tag}</TagLabel>
            </Tag>
          ))}
        </Flex>
      </Flex>
    </components.Option>
  );
};
